<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="gallery-modal"
      role="dialog"
      aria-modal="true"
      :aria-label="productName"
      @click.self="close"
    >
      <button type="button" class="gallery-modal__close" aria-label="Close gallery" @click="close">
        &times;
      </button>

      <button
        v-if="images.length > 1"
        type="button"
        class="gallery-modal__nav gallery-modal__nav--prev"
        aria-label="Previous image"
        @click="prev"
      >
        &#8249;
      </button>

      <div class="gallery-modal__stage">
        <img
          :src="images[currentIndex]"
          :alt="`${productName} image ${currentIndex + 1}`"
          class="gallery-modal__image"
        />
        <p class="gallery-modal__caption">
          Image {{ currentIndex + 1 }} / {{ images.length }}
          <span v-if="activeCaption"> — {{ activeCaption }}</span>
        </p>
      </div>

      <button
        v-if="images.length > 1"
        type="button"
        class="gallery-modal__nav gallery-modal__nav--next"
        aria-label="Next image"
        @click="next"
      >
        &#8250;
      </button>

      <div v-if="images.length > 1" class="gallery-modal__dots">
        <button
          v-for="(image, index) in images"
          :key="`${image}-${index}`"
          type="button"
          class="gallery-modal__dot"
          :class="{ 'is-active': index === currentIndex }"
          :aria-label="`Go to image ${index + 1}`"
          @click="goTo(index)"
        />
      </div>

      <div v-if="images.length > 1" class="gallery-modal__thumbs">
        <button
          v-for="(image, index) in images"
          :key="`thumb-${image}-${index}`"
          type="button"
          class="gallery-modal__thumb"
          :class="{ 'is-active': index === currentIndex }"
          @click="goTo(index)"
        >
          <img :src="image" :alt="`${productName} thumbnail ${index + 1}`" />
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ImageGalleryModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    images: {
      type: Array,
      default: () => []
    },
    startIndex: {
      type: Number,
      default: 0
    },
    productName: {
      type: String,
      default: ''
    },
    captions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close'],
  data() {
    return {
      currentIndex: 0
    };
  },
  computed: {
    activeCaption() {
      const caption = this.captions[this.currentIndex];
      if (caption && String(caption).trim() !== '') {
        return caption;
      }

      return this.productName;
    }
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.currentIndex = this.startIndex;
        document.body.classList.add('gallery-modal-open');
        window.addEventListener('keydown', this.onKeydown);
      } else {
        document.body.classList.remove('gallery-modal-open');
        window.removeEventListener('keydown', this.onKeydown);
      }
    },
    startIndex(index) {
      if (this.isOpen) {
        this.currentIndex = index;
      }
    }
  },
  beforeUnmount() {
    document.body.classList.remove('gallery-modal-open');
    window.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    close() {
      this.$emit('close');
    },
    prev() {
      const total = this.images.length;
      this.currentIndex = (this.currentIndex - 1 + total) % total;
    },
    next() {
      const total = this.images.length;
      this.currentIndex = (this.currentIndex + 1) % total;
    },
    goTo(index) {
      this.currentIndex = index;
    },
    onKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'ArrowLeft') {
        this.prev();
      } else if (event.key === 'ArrowRight') {
        this.next();
      }
    }
  }
});
</script>
